<template>
    <ModalWrapper v-model="modal" max-width="500" :loading="isLoading">
        <template v-slot:title>
            {{ company ? `${$t('company')} ${company.name}` : client ? $t('client') : '' }}
        </template>
        <template v-if="!isLoading">
            <template v-if="company">
                <v-list lines="two">
                    <v-list-item v-if="company.countryName" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('country') }}:</v-list-item-title>
                        <v-list-item-subtitle>{{ company.countryName }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="company.cityName" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('city') }}:</v-list-item-title>
                        <v-list-item-subtitle>{{ company.cityName }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="company.address" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('address') }}:</v-list-item-title>
                        <v-list-item-subtitle>{{ company.address }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="company.postalCode" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('postal_code') }}:</v-list-item-title>
                        <v-list-item-subtitle>{{ company.postalCode }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item
                        v-if="
                            company.contactPerson && (company.contactPerson.firstName || company.contactPerson.lastName)
                        "
                        lines="two"
                        class="pa-0"
                    >
                        <v-list-item-title>{{ $t('main_contact_person') }}:</v-list-item-title>
                        <v-list-item-subtitle>
                            <span v-if="company.contactPerson.prefix" v-html="company.contactPerson.prefix" />
                            <span v-if="company.contactPerson.firstName" v-html="company.contactPerson.firstName" />
                            <span v-if="company.contactPerson.middleName" v-html="company.contactPerson.middleName" />
                            <span v-if="company.contactPerson.lastName" v-html="company.contactPerson.lastName" />
                        </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="company.phones && company.phones.length" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('phone') }}:</v-list-item-title>
                        <v-list-item-subtitle class="opacity-100">
                            <span v-for="(phone, index) in company.phones" :key="phone">
                                <a :href="`tel:${phone}`">{{ phone }}</a>
                                <span v-if="index !== company.phones.length - 1 && company.phones.length > 1">, </span>
                            </span>
                        </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="company.emails && company.emails.length" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('email') }}:</v-list-item-title>
                        <v-list-item-subtitle class="opacity-100">
                            <span v-for="(email, index) in company.emails" :key="email">
                                <a :href="`mailto:${email}`">{{ email }}</a>
                                <span v-if="index !== company.emails.length - 1 && company.emails.length > 1">, </span>
                            </span>
                        </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="pricingRuleEntries.length" lines="two" class="pa-0">
                        <!-- TODO [P0] Need reimplement-->
                        <div v-if="pricingRuleEntries[0].pricingRuleEntries?.length" />
                        <v-treeview
                            v-if="pricingRuleEntries[0].pricingRuleEntries?.length"
                            :items="pricingRuleEntries"
                            item-children="pricingRuleEntries"
                            class="full-width"
                        >
                            <template v-slot:title="{item}">
                                <div v-if="item.ruleToken === 'rootRule'" class="d-flex">
                                    <div>{{ $t('markup_and_commission_settings') }}</div>
                                    <v-spacer />
                                    <div class="column-markup">{{ $t('markup_value') }}</div>
                                    <div class="column-commission">{{ $t('commission') }}</div>
                                </div>
                                <div v-else class="d-flex">
                                    <div class="text-capitalize text-truncate labelToken">{{ item.labelToken }}</div>
                                    <template v-if="!item.pricingRuleEntries || !item.pricingRuleEntries.length">
                                        <v-spacer />
                                        <div class="column-markup">
                                            {{
                                                item.markupValueType === 'PERCENTAGE'
                                                    ? `${item.markupValue}%`
                                                    : item.markupValueType === 'MARGIN'
                                                      ? `Margin ${item.markupValue}%`
                                                      : $filters.priceFormat(item.markupValue, salesSettings.currency)
                                            }}
                                        </div>
                                        <div class="column-commission">
                                            {{
                                                item.commissionInCurrency
                                                    ? $filters.priceFormat(item.commissionValue, salesSettings.currency)
                                                    : `${item.commissionValue}%`
                                            }}
                                        </div>
                                    </template>
                                </div>
                            </template>
                        </v-treeview>
                        <div v-else>
                            <div class="d-flex full-width">
                                <div>{{ $t('markup_and_commission_settings') }}</div>
                                <v-spacer />
                                <div class="column-markup">{{ $t('markup_value') }}</div>
                                <div class="column-commission">{{ $t('commission') }}</div>
                            </div>
                            <div class="d-flex full-width">
                                <div class="text-capitalize">{{ $t('all') }}</div>
                                <v-spacer />
                                <div class="column-markup">
                                    {{
                                        pricingRuleEntries[0].markupValueType === 'PERCENTAGE'
                                            ? `${pricingRuleEntries[0].markupValue}%`
                                            : pricingRuleEntries[0].markupValueType === 'MARGIN'
                                              ? `Margin ${pricingRuleEntries[0].markupValue}%`
                                              : $filters.priceFormat(
                                                    pricingRuleEntries[0].markupValue,
                                                    salesSettings.currency
                                                )
                                    }}
                                </div>
                                <div class="column-commission">
                                    {{
                                        pricingRuleEntries[0].commissionInCurrency
                                            ? $filters.priceFormat(
                                                  pricingRuleEntries[0].commissionValue,
                                                  salesSettings.currency
                                              )
                                            : `${pricingRuleEntries[0].commissionValue}%`
                                    }}
                                </div>
                            </div>
                        </div>
                    </v-list-item>
                    <v-list-item v-if="salesSettings" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('commission_VAT') }}:</v-list-item-title>
                        <v-list-item-subtitle>
                            {{ salesSettings.commissionVat || $t('no_VAT_applied') }}
                        </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="salesSettings && salesSettings.paymentPlan" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('payment_plan') }}:</v-list-item-title>
                        <v-list-item-subtitle>{{ salesSettings.paymentPlan }}</v-list-item-subtitle>
                    </v-list-item>
                </v-list>
            </template>
            <template v-else-if="client">
                <v-list lines="two">
                    <v-list-item v-if="client.firstName || client.lastName" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('name') }}:</v-list-item-title>
                        <v-list-item-subtitle>
                            <span
                                >{{ `${client.prefix || ''} ${client.firstName || ''} ${client.lastName || ''}` }}
                            </span>
                        </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="client.birthdate" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('birthdate') }}:</v-list-item-title>
                        <v-list-item-subtitle>
                            {{ client.birthdate }}
                        </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="client.travelPreferences" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('travel_preferences') }}:</v-list-item-title>
                        <v-list-item-subtitle>
                            {{ client.travelPreferences }}
                        </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="client.addressLine" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('address') }}:</v-list-item-title>
                        <v-list-item-subtitle>
                            {{ client.addressLine }}
                        </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="client.contactPhone" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('phone') }}:</v-list-item-title>
                        <v-list-item-subtitle class="opacity-100">
                            <a :href="`tel:${client.contactPhone}`">{{ client.contactPhone }}</a>
                        </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="client.email" lines="two" class="pa-0">
                        <v-list-item-title>{{ $t('email') }}:</v-list-item-title>
                        <v-list-item-subtitle class="opacity-100">
                            <a :href="`mailto:${client.email}`">{{ client.email }}</a>
                        </v-list-item-subtitle>
                    </v-list-item>
                </v-list>
            </template>
        </template>
    </ModalWrapper>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator'
    import {EventBus, SHOW_CONTACT_DETAILS_MODAL} from '@/utils/event-bus'
    import {VTreeview} from 'vuetify/labs/VTreeview'
    @Component({
        components: {VTreeview},
    })
    export class ContactDetailsModal extends Vue {
        modal = false
        isLoading = true
        company = null
        salesSettings = null
        client = null

        mounted() {
            EventBus.$on(SHOW_CONTACT_DETAILS_MODAL, this.showModal)
            this.company = null
            this.client = null
        }

        beforeUnmount() {
            EventBus.$off(SHOW_CONTACT_DETAILS_MODAL, this.showModal)
        }

        async showModal({type, order}) {
            this.isLoading = true
            this.company = null
            this.salesSettings = null
            this.client = null
            this.modal = true

            const rq = {}
            if (type === 'company') {
                rq.id = order.clientId
            } else if (type === 'client' && order.clientType === 'TA') {
                rq.id = order.orderClientInfo.clientId
                rq.companyId = order.clientId
            } else if (type === 'client' && order.clientType !== 'TA') {
                rq.id = order.orderClientInfo.clientId
            }

            if (type === 'company') {
                try {
                    await Promise.allSettled([
                        (async () => {
                            this.company = (await this.$api.companies.get(rq)).companies[0]
                        })(),
                        (async () => {
                            this.salesSettings = await this.$api.salesSettings.get({
                                clientCompanyId: rq.id,
                                ...(order.b2cId && {b2cId: order.b2cId}),
                            })
                        })(),
                    ])
                } catch (e) {
                    this.modal = false
                } finally {
                    this.isLoading = false
                }
            } else if (type === 'client') {
                try {
                    this.client = (await this.$api.privateClients.get(rq)).persons[0]
                } catch (e) {
                    this.modal = false
                } finally {
                    this.isLoading = false
                }
            }
        }

        get pricingRuleEntries() {
            const makeTree = pricingRuleEntries =>
                pricingRuleEntries.map(entry => {
                    const entryCopy = {...entry}

                    if (!entryCopy.pricingRuleEntries?.length) {
                        delete entryCopy.pricingRuleEntries
                    }
                    return {
                        ...entryCopy,
                        ...(entryCopy.pricingRuleEntries && {pricingRuleEntries: makeTree(entry.pricingRuleEntries)}),
                    }
                })
            return this.salesSettings?.pricingRuleEntries ? makeTree(this.salesSettings?.pricingRuleEntries) : []
        }
    }

    export default ContactDetailsModal
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    .column-markup,
    .column-commission {
        width: 150px;
        text-align: right;
    }

    .labelToken {
        max-width: 800px;
    }

    @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
        .column-markup,
        .column-commission {
            width: 80px;
            text-align: right;
        }

        .labelToken {
            max-width: 150px;
        }

        .v-treeview {
            font-size: 12px;
        }
    }
</style>
